import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Heart, Twitter, Linkedin, Github, Instagram } from './SketchIconPack'
import CurvedLoop from './CurvedLoop'
import ScrollFloat from './ScrollFloat'

const footerLinks = {
  Services: [
    { label: 'Custom Software', link: '/services' },
    { label: 'Web Applications', link: '/services' },
    { label: 'Mobile Apps', link: '/services' },
    { label: 'AI Solutions', link: '/services' },
    { label: 'Business Automation', link: '/services' },
    { label: 'Cloud Deployment', link: '/services' },
  ],
  Company: [
    { label: 'About Us', link: '/about' },
    { label: 'Our Solutions', link: '/solutions' },
    { label: 'Our Process', link: '/process' },
    { label: 'Portfolio', link: '/portfolio' },
    { label: 'Pricing', link: '/pricing' },
    { label: 'Contact', link: '/contact' }
  ],
  Resources: [
    { label: 'Blog', link: '/blog' },
    { label: 'Case Studies', link: '/portfolio' },
    { label: 'Documentation', link: '/docs' },
    { label: 'Support', link: '/contact' },
    { label: 'FAQ', link: '/faq' },
    { label: 'Privacy Policy', link: '/privacy' }
  ],
}

const socialLinks = [
  { name: 'Twitter', icon: Twitter, link: 'https://twitter.com' },
  { name: 'LinkedIn', icon: Linkedin, link: 'https://linkedin.com' },
  { name: 'GitHub', icon: Github, link: 'https://github.com' },
  { name: 'Instagram', icon: Instagram, link: 'https://www.instagram.com/akrlabs_official/' },
]

export default function Footer() {
  return (
    <footer className="relative pt-20 pb-8 border-t-2 border-solid border-[var(--color-border)] overflow-hidden">
      {/* Curved Loop Banner */}
      <div className="w-[100vw] relative left-1/2 -translate-x-1/2 overflow-hidden flex justify-center mt-8 mb-16">
        <CurvedLoop
          marqueeText="LET'S BUILD SOMETHING GREAT ✦ JOIN THE COMMUNITY ✦ START BUILDING ✦ "
          speed={1}
          curveAmount={-100}
          interactive={false}
          className="pointer-events-none"
          yOffset={120}
        />
      </div>

      <div className="container relative z-10">
        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <ScrollFloat
            containerClassName="mb-6"
            textClassName="text-[clamp(2rem,5vw,3.5rem)] font-bold font-[var(--font-heading)] leading-[1.1]"
            highlightWords={['Next', 'Thing']}
            highlightClassName="text-[var(--color-red)]"
          >
            Ready to Build the Next Big Thing?
          </ScrollFloat>
          <p className="text-[var(--color-text-muted)] max-w-xl mx-auto mb-8">
            Partner with us to engineer your next big software product.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-flex ink-splash"
          >
            <span className="flex items-center gap-2">
              Start Your Project
              <ArrowUpRight size={18} />
            </span>
          </Link>
        </motion.div>

        <div className="line-separator mb-12" />

        {/* Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-8">
              <img src="/logo.webp" alt="AKR Labs" className="h-28 w-auto object-contain rounded-2xl" loading="lazy" />
            </Link>
            <p className="text-sm text-[var(--color-text-dim)] leading-relaxed mb-6">
              A technology partner dedicated to engineering scalable software products that empower modern businesses and accelerate digital transformation.
            </p>
            <div className="flex gap-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-[3px] border-2 border-[var(--color-sketch-border)] flex items-center justify-center text-[var(--color-text-dim)] hover:text-[var(--color-red)] hover:border-[var(--color-red)] shadow-[2px_2px_0px_var(--color-sketch-shadow)] hover:shadow-[0px_0px_8px_var(--color-red),_2px_2px_0px_var(--color-red)] transition-all duration-300 group bg-[var(--color-bg-primary)]"
                    aria-label={social.name}
                  >
                    <Icon size={18} className="group-hover:scale-110 transition-transform duration-300" />
                  </a>
                )
              })}
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h3 className="text-sm font-bold font-[var(--font-heading)] text-[var(--color-red)] mb-4">{title}</h3>
              <ul className="space-y-2.5">
                {links.map((item) => (
                  <li key={item.label}>
                    {item.link.startsWith('/#') || item.link.startsWith('#') || item.link.startsWith('http') ? (
                      <a 
                        href={item.link} 
                        onClick={(e) => {
                          if (item.link.startsWith('/#') && window.location.pathname === '/') {
                            e.preventDefault();
                            document.getElementById(item.link.substring(2))?.scrollIntoView({ behavior: 'smooth' });
                            window.history.pushState(null, '', item.link);
                          }
                        }}
                        className="text-sm text-[var(--color-text-dim)] hover:text-[var(--color-gold)] transition-colors duration-300 sketch-underline"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link to={item.link} className="text-sm text-[var(--color-text-dim)] hover:text-[var(--color-gold)] transition-colors duration-300 sketch-underline">
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="line-separator mb-8" />

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[var(--color-text-dim)]">
            © {new Date().getFullYear()} AKR Labs. All rights reserved.
          </p>
          <p className="text-xs text-[var(--color-text-dim)] flex items-center gap-1">
            Crafted with <Heart size={12} className="text-[var(--color-red)]" fill="currentColor" /> by AKR Labs
          </p>
        </div>
      </div>
    </footer>
  )
}
